import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import { HELP_PHONEBOOKS } from "@/constants/HelpUrls";
import { NEW_PHONEBOOK } from "@/constants/New";
import urls from "@/constants/Urls";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputCopy from "@/design/forms/InputCopy";
import InputSelect from "@/design/forms/InputSelect";
import InputText from "@/design/forms/InputText";
import Page from "@/design/layouts/Page";
import { showSnackbar } from "@/store/reducers/app";
import { setHelpUrl } from "@/store/reducers/ui";
import { string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, {
  viAddressBook,
  viArrowLeft,
  viArrowRightToFile,
  viGear,
  viPersonXmark,
  viPlus,
  viSpinnerThird,
} from "@sippy-platform/valkyrie";

import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, IconButton, Stack, Typography } from "@mui/material";
import { format } from "date-fns";
import fileDownload from "js-file-download";

import ContactGroupCard from "./ContactGroupCard";
import ImportPhonebook from "./ImportPhonebook";
import phonebookReducer from "./phonebookReducer";

function Phonebook() {
  const { id, pbxId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [client, setClient] = useState("Zoiper");
  const [tab, setTab] = useState(0);

  const pbx = useSelector((state) => state.domain.pbx);

  useEffect(() => {
    dispatch(setHelpUrl(HELP_PHONEBOOKS));
  }, [dispatch]);

  const handleTab = (event, newTab) => setTab(newTab);

  const {
    value: phonebook,
    dispatch: phonebookDispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_PHONEBOOK,
    reducer: phonebookReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 60], { message: "Name must be at most 60 characters." }),
    }),
    onSubmit: () => Api.storePhonebook(phonebook),
    onSuccess: (response) => {
      if (!phonebook.id) {
        phonebookDispatch({ type: "id", value: response.data.phonebook.id });
        navigate(`../${response.data.phonebook.id}`);
      }
    },
  });

  // Fetch the phonebook, but only if an ID is given
  useEffect(() => {
    if (id !== "new") {
      toggleLoadingState(true);

      Promise.resolve(Api.getPhonebook({ id }))
        .then((_response) => phonebookDispatch({ type: "set", value: _response.data.phonebook }))
        .finally(toggleLoadingState);
    }
  }, [id, phonebookDispatch]);

  function refreshPhonebookData() {
    Promise.resolve(Api.getPhonebook({ id })).then((_response) => {
      phonebookDispatch({ type: "set", value: _response.data.phonebook });
    });
  }

  // Handle changes
  const formHandler = (property, value) => {
    phonebookDispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  function onDeleteFinish() {
    Promise.resolve(Api.getPhonebooks()).then(() => navigate(`/pbx/${pbxId}/phonebooks`));
  }

  // Export
  const [downloadExport, setDownloadExport] = useState(false);

  function getPhonebookExport() {
    setDownloadExport(true);

    Promise.resolve(Api.exportPhonebook(phonebook, "csv"))
      .then((response) => {
        fileDownload(response.data, `${phonebook.name} ${format(new Date(), "yyyy-MM-dd")}.csv`);
        setDownloadExport(false);
      })
      .finally(() => setDownloadExport(false));
  }

  // Prune
  const [showPruneDialog, setShowPruneDialog] = useState(false);
  const [pruningPhonebook, setPruningPhonebook] = useState(false);

  function prunePhonebook() {
    setPruningPhonebook(true);
    setShowPruneDialog(false);

    Promise.resolve(Api.prunePhonebook(phonebook))
      .then(() => {
        dispatch(showSnackbar("Phonebook successfully pruned."));
        setPruningPhonebook(false);
        refreshPhonebookData();
      })
      .finally(() => setPruningPhonebook(false));
  }

  return (
    <Page maxWidth="lg">
      <title>{`${phonebook.name || "Phonebook"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          tab={tab}
          changeTab={handleTab}
          tabs={[
            { label: "Phonebook", icon: viAddressBook, id: "phonebook" },
            { label: "Manage", icon: viGear, id: "manage", disabled: !phonebook.id },
          ]}
          avatar={
            <IconButton variant="avatar" component={NavLink} to={`/pbx/${pbxId}/phonebooks`}>
              <Valkyrie icon={viArrowLeft} />
              <Avatar text={phonebook.name} icon={viAddressBook} />
            </IconButton>
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              <DeleteFlow
                key={phonebook.id || "nk"}
                isActionable={!!phonebook?.id}
                onDelete={() => Api.deletePhonebook(phonebook)}
                name={phonebook.name}
                deleteInfo="Are you sure you want to delete this phonebook?"
                onDeleteFinish={onDeleteFinish}
              />
            </>
          }
          title={phonebook.name || "Unnamed phonebook"}
        />
        {tab === 0 && (
          <>
            <FormGroup title="Identity">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputText
                  required
                  id="name"
                  label="Name"
                  onChange={formHandler}
                  value={phonebook.name}
                  error={errors}
                />
              </Grid>
            </FormGroup>
            {phonebook.id && (
              <FormGroup
                title="Contact groups"
                actions={
                  <Button component={NavLink} to={`new`} startIcon={<Valkyrie icon={viPlus} />} variant="contained">
                    Add
                  </Button>
                }
              >
                <Grid size={12}>
                  {phonebook.groups.length ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(min(16rem, 100%), 1fr))",
                        gap: 1,
                      }}
                    >
                      {phonebook.groups.map((contactGroup) => (
                        <ContactGroupCard contactGroup={contactGroup} phonebook={phonebook} key={contactGroup.id} />
                      ))}
                    </Box>
                  ) : (
                    <Typography>This phonebook has no groups yet.</Typography>
                  )}
                </Grid>
              </FormGroup>
            )}
          </>
        )}
        {tab === 1 && (
          <>
            <ImportPhonebook phonebook={phonebook} refreshPhonebookData={refreshPhonebookData} />
            <FormGroup
              title="Export"
              actions={
                <Button
                  color="primary"
                  variant="contained"
                  onClick={getPhonebookExport}
                  startIcon={
                    <Valkyrie icon={downloadExport ? viSpinnerThird : viArrowRightToFile} spin={downloadExport} />
                  }
                >
                  Export
                </Button>
              }
            />
            <FormGroup title="Prune" collapsible={true}>
              <Grid size={12}>
                <Typography sx={{ mb: 1 }}>
                  Remove all contacts and groups from your phonebook, but leave the phonebook intact.
                </Typography>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => setShowPruneDialog(true)}
                  startIcon={
                    <Valkyrie icon={pruningPhonebook ? viSpinnerThird : viPersonXmark} spin={pruningPhonebook} />
                  }
                >
                  Prune
                </Button>
                <Dialog open={showPruneDialog} onClose={() => setShowPruneDialog(false)} scroll="paper">
                  <Stack
                    sx={{
                      gap: 2,
                    }}
                  >
                    <DialogTitle sx={{ p: 0 }}>Prune {phonebook.name}</DialogTitle>
                    <DialogContent sx={{ p: 0 }}>
                      <Typography>
                        Are you sure you want to prune this phonebook? All your groups and contacts will be removed.
                      </Typography>
                    </DialogContent>
                    <Stack
                      direction="row"
                      sx={{
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Button onClick={() => setShowPruneDialog(false)} variant="outlined">
                        Cancel
                      </Button>
                      <Button
                        onClick={() => prunePhonebook()}
                        autoFocus
                        variant="contained"
                        color="error"
                        startIcon={<Valkyrie icon={viPersonXmark} />}
                      >
                        Prune
                      </Button>
                    </Stack>
                  </Stack>
                </Dialog>
              </Grid>
            </FormGroup>
            <FormGroup title="Usage">
              <Grid size={12}>
                <Stack
                  direction="row"
                  sx={{
                    alignItems: "stretch",
                    gap: 1,
                    height: 40,
                  }}
                >
                  <InputSelect
                    id="client"
                    label="Client"
                    error={errors}
                    onChange={(_, value) => setClient(value)}
                    value={client}
                    selects={[
                      { value: "Fanvil" },
                      { value: "Gigaset" },
                      { value: "Snom" },
                      { value: "Yealink" },
                      { value: "Zoiper" },
                    ]}
                    getOptionLabel={(option) => option.value}
                    sx={{ width: 180 }}
                  />
                  <InputCopy
                    id="phonebookUrl"
                    label="Phonebook URL"
                    value={
                      phonebook.id
                        ? `${urls.phonebook}?id=${phonebook.id}&client=${client.toLowerCase()}&token=${phonebook.token}`
                        : ""
                    }
                  />
                </Stack>
              </Grid>
            </FormGroup>
          </>
        )}
      </Stack>
    </Page>
  );
}

export default Phonebook;
